import { useQuery } from '@apollo/client/react';
import { AlertCircle, Info, Loader2 } from 'lucide-react';
import { STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS } from '@staff-app/graphql-client';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@staff-app/ui';
import { formatUptimeDuration } from '../lib/device-metrics';
import { parseDeviceLiveInfo } from '../lib/parse-device-info';
import { useDrawerScopeVars } from '../../../lib/drawer-mode';

interface Result {
  staffProxyTestingDeviceMdmDetails: string | null;
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="break-all text-right font-medium">{value}</span>
    </div>
  );
}

/**
 * More › View device info — port of scio3 `MdmDeviceInfoDialog` (native path): asks the device itself
 * via `mdmDetails(GET_DEVICE_INFO)` with `fresh: true` (bypasses the 15s server cache), parsed by the
 * drawer's shared `parseDeviceLiveInfo`. Read-only — the device reports, nothing changes.
 */
export function TestingDeviceInfoDialog({
  open,
  deviceId,
  onClose,
}: {
  open: boolean;
  deviceId: string;
  onClose: () => void;
}) {
  const scope = useDrawerScopeVars();
  const { data, loading, error, refetch } = useQuery<Result>(
    STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS,
    {
      variables: { deviceId, command: 'GET_DEVICE_INFO', fresh: true, ...scope },
      skip: !open || !deviceId,
      fetchPolicy: 'network-only',
    },
  );

  const info = parseDeviceLiveInfo(data?.staffProxyTestingDeviceMdmDetails);
  const resolution =
    info?.displayWidth && info?.displayHeight
      ? `${info.displayWidth} × ${info.displayHeight}`
      : null;
  const refresh = info?.refreshRate ? `${parseFloat(info.refreshRate).toFixed(2)} Hz` : null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="sm:max-w-md"
        aria-describedby={undefined}
        data-testid="testing-device-info-dialog"
      >
        <DialogHeader variant="muted">
          <DialogTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-muted-foreground" />
            {'Device info'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1 py-2">
          {loading ? (
            <div className="flex flex-col items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              {'Asking the device…'}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-muted-foreground">
                {'Failed to load device info. '}
                {error.message}
              </p>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                {'Retry'}
              </Button>
            </div>
          ) : info ? (
            <div
              className="space-y-0.5 rounded-md border p-4"
              data-testid="testing-device-info-fields"
            >
              <InfoRow label={'Device serial no.'} value={info.serialNumber} />
              <InfoRow label={'WiFi access point'} value={info.wifiSSID} />
              <InfoRow label={'Display resolution'} value={resolution} />
              <InfoRow label={'Display refresh rate'} value={refresh} />
              <InfoRow label={'RAM usage'} value={info.ramUsage} />
              <InfoRow label={'RAM total'} value={info.ramTotal} />
              <InfoRow label={'Storage usage'} value={info.storageUsage} />
              <InfoRow label={'Storage total'} value={info.storageTotal} />
              <InfoRow label={'Temperature'} value={info.temperature} />
              <InfoRow label={'Uptime'} value={formatUptimeDuration(info.uptimeSeconds)} />
              <InfoRow
                label={'Battery'}
                value={info.batteryPercent != null ? `${info.batteryPercent}%` : null}
              />
            </div>
          ) : data ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <p className="text-sm text-muted-foreground">
                {'The device did not answer with any device info.'}
              </p>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                {'Ask again'}
              </Button>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
